import { useLazyQuery, useMutation } from '@apollo/client';
import {
  ActionGroup,
  ALL_MODULES,
  BackgroundIcon,
  ChooseAttachments,
  DeleteModal,
  getDownloadURL,
  getHeaderValue,
  HeaderTag,
  Loader,
  toast,
  getTranslatedText,
  AppContext,
  FeatureGuide,
  RMM_ONLY_PLANS,
  AccessControl,
  isAuthorised,
  useCheckFeatureGating,
  fetchApi,
  getLokaliseKeyByProduct
} from '@nile/components';
import {
  GET_AGENT_CUSTOMISATION,
  UPDATE_AGENT_CUSTOMISATION,
  RMMClient
} from '@nile/graphql';
import envConfig from '@nile/home/src/Config/env';
import {
  Card,
  Heading,
  Modal,
  Page,
  Banner,
  Sheet,
  TextContainer,
  TextField,
  TextStyle,
  ToggleButton,
  DisplayText,
  Popover,
  Button,
  ActionList,
  ResourceItem
} from '@superops/ui-design-system';
import { SVGIcon } from '@superops/super-icons';
import produce from 'immer';
import { useFlags, withLDConsumer } from 'launchdarkly-react-client-sdk';
import { cloneDeep, isEmpty } from 'lodash';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { FormattedMessage, injectIntl } from 'react-intl';
import { withRouter, Prompt } from 'react-router-dom';
import { ReactSortable } from 'react-sortablejs';
import { Tooltip } from 'react-clone-tippy';
import { ContentWrapper } from 'sopslibrary';
import styled from 'styled-components';
import * as _ from 'lodash';
import dompurify from 'dompurify';
import SystemTrayMenuOptionForm from '@nile/components/src/Forms/SystemTrayMenuOptionFormNew';
import { dompurifier } from '@nile/components/src/Utils/helpers';

const UploadButton = styled.div`
  cursor: pointer;
  color: #000ff0;
  position: relative;
  min-width: 80px;
  min-height: 80px;
  flex-direction: column;

  .org-logo {
    height: 80px;
    width: 100px;
    padding: 14px 28px 14px 21px;
    border: 1px solid #dcdcdc;
    border-radius: 12px;
    margin-bottom: 5px;
  }
  .logo {
    height: 60px;
    object-fit: contain;
  }
  .reduced-opacity {
    opacity: 0.4;
  }
`;

export const AVAILABLE_TRACY_ICON_OPTIONS = [
  'ABOUT',
  'STATIC_TEXT',
  'CREATE_TICKET',
  'URL',
  'EXECUTE_SCRIPT'
];

export const TRACY_ICON_ACTION_ITEMS = {
  ABOUT: {
    name: 'ABOUT',
    label: 'admin.systemTrayIcon.menuItems.about',
    icon: 'About'
  },
  STATIC_TEXT: {
    name: 'STATIC',
    label: 'admin.systemTrayIcon.menuItems.static',
    icon: 'Notes'
  },
  CREATE_TICKET: {
    name: 'TKTFORM',
    label: 'admin.systemTrayIcon.menuItems.ticketForm',
    icon: 'TicketOutline'
  },
  URL: {
    name: 'URL',
    label: 'admin.systemTrayIcon.menuItems.url',
    icon: 'LinkDark'
  },
  EXECUTE_SCRIPT: {
    name: 'EXECCMD',
    label: 'admin.systemTrayIcon.menuItems.executeCmd',
    icon: 'Script'
  }
};
export const AVAILABLE_TRACY_ICON_OPTIONS2 = [
  {
    value: 'ABOUT',
    content: 'About',
    iconName: 'About'
  },
  {
    value: 'STATIC_TEXT',
    content: 'Static text',
    iconName: 'Notes'
  },
  {
    value: 'CREATE_TICKET',
    content: 'Ticket form',
    iconName: 'TicketOutline'
  },
  {
    value: 'URL',
    content: 'URL',
    iconName: 'LinkDark'
  },
  {
    value: 'EXECUTE_SCRIPT',
    content: 'Execute command',
    iconName: 'Script'
  }
];

const Attachments = (props) => {
  const [active, setActive] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState(null);
  const {
    modalActivator,
    agentCustomizationData,
    downloadURL,
    attachmentType,
    nestedObject,
    isSystemTrayEditAccess
  } = props;
  const [loading, setLoading] = useState(props.loading);
  useEffect(() => {
    setLoading(props.loading);
  }, [props.loading]);

  const url = agentCustomizationData?.logoFileName
    ? getDownloadURL({
        module: attachmentType,
        details: {
          fileName: agentCustomizationData?.logoFileName
        }
      })
    : null;

  const handleChange = useCallback(() => setActive(!active), [active]);

  const uploadDocuments = useCallback(() => {
    props.handleUpload(
      uploadedFiles,
      attachmentType,
      nestedObject,
      handleChange
    );
    setUploadedFiles(null);
  });

  const handleCancel = useCallback(() => {
    setUploadedFiles(null);
    handleChange();
  });

  const updateFiles = (updatedFiles) => {
    setUploadedFiles(updatedFiles);
  };

  const getImageDimentions = (file) => {
    const reader = new FileReader();
    return new Promise((resolve, reject) => {
      let width;
      let height;

      const onError = () => {
        reject({ height, width });
      };

      reader.onload = (file) => {
        const image = new Image();
        image.src = reader.result;
        // get image dimensions
        image.onload = function() {
          height = this.height;
          width = this.width;
          resolve({ height, width });
        };

        image.onerror = onError;
        image.onabort = onError;
      };
      reader.onerror = onError;
      reader.onabort = onError;
      // reads the file
      reader.readAsDataURL(file);
    });
  };

  const customValidator = async (files) => {
    let validatedFiles = [...files];

    const filterPromises = files.map((file) =>
      file.type.startsWith('image/') ? getImageDimentions(file) : true
    );
    const resolvedFilterPromises = await Promise.all(filterPromises);

    validatedFiles = files.filter((file, idx) => {
      if (file.type.startsWith('image/')) {
        const {
          height: imageHeight,
          width: imageWidth
        } = resolvedFilterPromises[idx];
        const isDimensionValid = !(imageHeight > 80 || imageWidth > 80);
        if (!isDimensionValid)
          toast(
            'warning',
            getTranslatedText(props.intl, 'admin.upload.dimensions.max.error', {
              height: 80,
              width: 80
            })
          );
        return isDimensionValid;
      }
    });

    return validatedFiles;
  };

  return (
    <div>
      {modalActivator ? (
        <div
          onClick={() => isSystemTrayEditAccess && handleChange()}
          id={props.id}
        >
          {modalActivator}
        </div>
      ) : (
        <>
          <UploadButton
            className="flex actr jctr"
            onClick={() => isSystemTrayEditAccess && handleChange()}
            id={props.id}
          >
            <div className="org-logo flex center ">
              <img
                src={
                  downloadURL
                    ? `${downloadURL}`
                    : url
                    ? `${url}`
                    : `${envConfig.imageRoot}svg/Org.svg`
                }
                className={
                  !downloadURL && !url ? 'logo reduced-opacity' : 'logo'
                }
              />
            </div>
            {isSystemTrayEditAccess && (
              <TextContainer>
                <FormattedMessage id="admin.agent.rebranding.upload.png" />
              </TextContainer>
            )}
          </UploadButton>
        </>
      )}
      <Modal
        open={active}
        onClose={handleChange}
        title={props.title}
        primaryAction={{
          content: <FormattedMessage id="admin.mspDetails.modal.upload" />,
          loading: loading,
          onAction: uploadDocuments
        }}
        secondaryActions={[
          {
            content: <FormattedMessage id="common.cancel" />,
            onAction: handleCancel
          }
        ]}
      >
        <Modal.Section>
          <div className="mb-10">
            <TextStyle weight="SemiBold">
              <FormattedMessage id="admin.agent.rebrand.logo.size" />
            </TextStyle>
          </div>
          <ChooseAttachments
            validTypes="image/png"
            type="image"
            singleUpload
            updateFiles={updateFiles}
            customValidator={customValidator}
          />
        </Modal.Section>
      </Modal>
    </div>
  );
};

function TrayIconSelect(props) {
  const { addNewMenuItem, planId } = props;
  const [popoverActive, setPopoverActive] = useState(false);
  const [option, setOption] = useState(AVAILABLE_TRACY_ICON_OPTIONS2);
  const flags = useFlags();
  const isRMMOnlyPlan =
    !useCheckFeatureGating('TICKETING')?.enabled &&
    useCheckFeatureGating('WINDOWS_MANAGEMENT')?.enabled;

  useEffect(() => {
    if (isRMMOnlyPlan) {
      const filteredMap = AVAILABLE_TRACY_ICON_OPTIONS2.filter(
        (el) => el.value !== 'CREATE_TICKET'
      );
      setOption(filteredMap);
    }
  }, []);

  function togglePopover() {
    setPopoverActive(!popoverActive);
  }

  const optionWithActions = option.map((opt, i) => {
    return {
      ...opt,
      onAction: () => addNewMenuItem(opt.value, i)
    };
  });

  return (
    <div>
      <Popover
        active={popoverActive}
        activator={
          <Button
            onClick={togglePopover}
            icon={<SVGIcon name="Add" />}
            disclosure
          >
            <FormattedMessage id="admin.systemTrayIcon.menuItems.dropdown" />
          </Button>
        }
        onClose={togglePopover}
      >
        <ActionList actionRole="menuitem" items={optionWithActions} />
      </Popover>
    </div>
  );
}

function SystemTrayIconPage(props) {
  const { intl } = props;

  const flags = useFlags();

  const [agentCustomizationData, setAgentCustomizationData] = useState({
    trayIcon: {}
  });

  const [changesPayload, setChangesPayload] = useState({});

  const [trayIconData, setTrayIconData] = useState({});
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [reOrdered, setReOrdered] = useState(false);

  const [primaryLoading, setPrimaryLoading] = useState(false);
  const [downloadURL, setDownloadURL] = useState({
    trayIcon: '',
    installerIcon: ''
  });

  const appContext = useContext(AppContext);
  const { user, currentProduct } = appContext;
  const roleFeatures = user?.role?.roleFeatureList || [];
  const isSystemTrayEditAccess = isAuthorised(
    roleFeatures,
    'systemTrayIcon.edit'
  );
  const planId = user?.chargeBeePlanId?.toLowerCase();

  const AVAILABLE_OS_TYPE = [
    {
      name: 'WINDOWS',
      title: getTranslatedText(intl, 'admin.systemTrayIcon.osType.windowsTray'),
      icon: 'WindowsDark'
    },
    {
      name: 'MAC',
      title: getTranslatedText(intl, 'admin.systemTrayIcon.osType.macTray'),
      icon: 'MacDark'
    }
  ];

  const [sheetData, setSheetData] = useState({
    open: false,
    menuData: {}
  });

  const toggleSheetActive = () => {
    setSheetData({
      ...sheetData,
      open: !open
    });
  };

  const sortAndSave = (data) => {
    const nextState = produce(data, (draft) => {
      draft.trayIcon.menus = _.sortBy(data.trayIcon.menus, ['menuOrder']);
    });
    setAgentCustomizationData(nextState);
  };

  const updateData = (data) => {
    sortAndSave(data);
    toast('success', 'admin.system.tray.icon.success.message');
    toggleSheetActive();
  };

  const handleClick = (menuData) => {
    setSheetData({
      menuData,
      open: true
    });
  };

  const [getAgentCustomization] = useLazyQuery(GET_AGENT_CUSTOMISATION, {
    client: RMMClient,
    fetchPolicy: 'network-only',
    onCompleted: (res) => {
      if (res && res.getAgentCustomisation) {
        setLoading(false);
        sortAndSave(res.getAgentCustomisation);
      }
    }
  });

  const [updateAgentCustomization] = useMutation(UPDATE_AGENT_CUSTOMISATION, {
    client: RMMClient,
    onCompleted: (res) => {
      if (res && res.updateAgentCustomisation) {
        sortAndSave(res.updateAgentCustomisation);
        setPrimaryLoading(false);
        setChangesPayload({});
        toast('success', 'admin.system.agent.rebrand.success.message');
      }
    }
  });

  useEffect(() => {
    getAgentCustomization();
  }, []);

  const addNewMenuItem = (option, index) => {
    console.log(option, 'Option');
    const menuData = {
      type: option,
      name: '',
      toolTipText: '',
      aboutMessage: '',
      url: '',
      menuOrder: index + 1
    };
    setSheetData({
      ...sheetData,
      open: true,
      menuData
    });
  };

  const handleUpload = (image, module, nestedObject, callback) => {
    if (image) {
      setActionLoading(true);
      const fileData = new FormData();
      image.forEach((doc, idx) => {
        fileData.append(`files`, doc);
      });
      fileData.append('module', module);

      const requestOptions = {
        method: 'POST',
        credentials: 'include',
        body: fileData,
        headers: getHeaderValue()
      };
      fetchApi(`${envConfig.fileRoot}/upload`, requestOptions)
        .then((response) => {
          return response.json();
        })
        .then((res) => {
          setActionLoading(false);
          if (res?.data?.downloadURL) {
            setAgentCustomizationData({
              ...agentCustomizationData,
              [nestedObject]: {
                ...agentCustomizationData[nestedObject],
                logoFileName: res?.data?.fileName
              }
            });

            setChangesPayload({
              ...changesPayload,
              [nestedObject]: {
                ...agentCustomizationData[nestedObject],
                logoFileName: res?.data?.fileName
              }
            });

            setDownloadURL({
              ...downloadURL,
              [nestedObject]: res?.data?.downloadURL
            });
          }
          callback();
        });
    }
  };

  const onInputChange = (data, nestedObject = '', type) => {
    // State maintain for ui state changes
    setAgentCustomizationData({
      ...agentCustomizationData,
      [nestedObject]: {
        ...agentCustomizationData[nestedObject],
        [type]: data
      }
    });
    const payloadData = agentCustomizationData[nestedObject];
    // State maintain for payload changes
    setChangesPayload({
      ...changesPayload,
      [nestedObject]: {
        ...payloadData,
        [type]: data
      }
    });
  };

  const saveTrayIcon = () => {
    if (!isEmpty(changesPayload) || reOrdered) {
      setPrimaryLoading(true);
      let clonedAgentData = cloneDeep(changesPayload);
      let nextState = {};

      if (clonedAgentData?.trayIcon) {
        nextState = produce(
          agentCustomizationData?.trayIcon?.menus,
          (draft) => {
            draft.map((item) => {
              delete item.scripts;
              return item;
            });
          }
        );

        delete clonedAgentData['trayIcon']['menus'];

        updateAgentCustomization({
          variables: {
            input: {
              ...clonedAgentData,
              trayIcon: {
                ...clonedAgentData.trayIcon,
                updateMenu: nextState
              }
            }
          }
        });
      } else {
        updateAgentCustomization({
          variables: {
            input: {
              ...clonedAgentData
            }
          }
        });
      }
    }
  };

  const deleteMenuItem = (deleteMenu) => {
    const nextState = produce(deleteMenu, (draft) => {
      delete draft.scripts;
    });

    let clonedAgentData = cloneDeep(agentCustomizationData);

    delete clonedAgentData['trayIcon']['menus'];

    updateAgentCustomization({
      variables: {
        input: {
          trayIcon: {
            ...clonedAgentData.trayIcon,
            deleteMenu: nextState
          }
        }
      }
    });
  };

  const setSortedList = (data) => {
    if (data && data.length) {
      setReOrdered(true);

      const menuChanges = data.map((item, index) => {
        delete item.chosen;
        delete item.selected;
        return { ...item, menuOrder: index + 1 };
      });

      setAgentCustomizationData({
        ...agentCustomizationData,
        trayIcon: {
          ...agentCustomizationData.trayIcon,
          menus: menuChanges
        }
      });

      setChangesPayload({
        ...changesPayload,
        trayIcon: {
          ...agentCustomizationData.trayIcon,
          menus: menuChanges
        }
      });
    }
  };

  const enabledRebranding = (active, nestedObject) => {
    setAgentCustomizationData({
      ...agentCustomizationData,
      [nestedObject]: {
        ...agentCustomizationData[nestedObject],
        enabled: active
      }
    });

    setChangesPayload({
      ...changesPayload,
      [nestedObject]: {
        ...agentCustomizationData[nestedObject],
        enabled: active
      }
    });
  };

  const filterScriptsWithoutData = (item, osType) => {
    if (item.type === 'EXECUTE_SCRIPT') {
      let isScriptPresent = item?.scripts?.find(
        (scriptItem) => scriptItem.type === osType.name
      );
      return isScriptPresent ? item : 0;
    }

    return item;
  };

  if (loading) return <Loader />;
  return (
    <div className="full-height p20 white-grey-page">
      <Page
        variant="secondary"
        contentHeight="120px"
        divider
        title={
          <TextStyle weight="Bold">
            {' '}
            <FormattedMessage id="admin.agentBranding.header" />
          </TextStyle>
        }
        subtitle={
          <>
            <TextContainer>
              <TextStyle variation="subdued">
                <FormattedMessage
                  id={getLokaliseKeyByProduct(currentProduct, 'systemTrayDesc')}
                />
              </TextStyle>
            </TextContainer>
            <div className="mt10">
              <FeatureGuide
                featureName="systemtrayicon"
                article
                // label="app.article.systemTray"
              />
            </div>
          </>
        }
        primaryAction={
          <div className="flex-item-fixed">
            <AccessControl
              roleToCheck="systemTrayIcon.edit"
              render={
                <div className="d-in-f">
                  <ActionGroup
                    onlyPrimary
                    primaryText={<FormattedMessage id="common.save" />}
                    primaryAction={saveTrayIcon}
                    primaryLoading={primaryLoading}
                  />
                </div>
              }
            />
          </div>
        }
      >
        <div className="white-transparent-bg flex g20 col w100 wrap">
          <div className="p2040">
            {/* MSI rebranding */}
            <div className="fx actr fx-sb mb-20">
              <Heading element="h2" weight="Bold">
                <FormattedMessage id="admin.settings.branding.agent" />
              </Heading>
              <AccessControl
                roleToCheck="systemTrayIcon.edit"
                render={
                  <ToggleButton
                    size="small"
                    onChange={(data) =>
                      enabledRebranding(data, 'installerIcon')
                    }
                    value={agentCustomizationData?.installerIcon?.enabled}
                    labels={{
                      on: getTranslatedText(
                        intl,
                        'admin.systemTrayIcon.enabled.status'
                      ),
                      off: getTranslatedText(
                        intl,
                        'admin.systemTrayIcon.disabled.status'
                      )
                    }}
                  />
                }
              />
            </div>
            <div className="flex actr mb-30">
              <div className="mr-20">
                <Attachments
                  nestedObject={'installerIcon'}
                  attachmentType={ALL_MODULES.agentCustomBrandImage}
                  loading={actionLoading}
                  agentCustomizationData={agentCustomizationData.installerIcon}
                  downloadURL={downloadURL.installerIcon}
                  handleUpload={handleUpload}
                  intl={props.intl}
                  description={
                    <FormattedMessage id="admin.mspDetails.logo.desc" />
                  }
                  title={<FormattedMessage id="admin.mspDetails.logo.title" />}
                  isSystemTrayEditAccess={isSystemTrayEditAccess}
                />
              </div>
              <div className="f11 mr-100">
                <div className="mt-m-20 mw-350 ">
                  <TextField
                    fontSize="fontLarge"
                    size="large"
                    noborder
                    placeholder={getTranslatedText(
                      intl,
                      'admin.settings.agent.input.placeholder'
                    )}
                    value={agentCustomizationData?.installerIcon?.name}
                    onChange={(data) =>
                      onInputChange(data, 'installerIcon', 'name')
                    }
                    disabled={!isSystemTrayEditAccess}
                  />
                </div>
              </div>
            </div>
            <div className="mt20">
              <Banner status="info">
                <DisplayText>
                  <FormattedMessage
                    id={'admin.msi.branding.banner.messageNew'}
                  />
                </DisplayText>
              </Banner>
            </div>
            <div className="mt20">
              <Card variant="secondaryCard" padding="sizeSmall">
                <div className="flex">
                  <SVGIcon name="Agent" size="xl" />
                  <SVGIcon name="ArrowRight" />
                  <SVGIcon name="PolicyLight" size="xl" />
                </div>
                <div className="mt10">
                  <DisplayText>
                    <FormattedMessage
                      id={getLokaliseKeyByProduct(
                        currentProduct,
                        'systemTrayConfig'
                      )}
                    />
                  </DisplayText>
                </div>
              </Card>
            </div>
          </div>
          {/* <div className="divider" />
          <div className="p2040">
            <div className="fx actr fx-sb mb-20">
              <Heading element="h3" weight="Bold">
                <FormattedMessage id="admin.settings.branding.systemtry" />
              </Heading>
              <AccessControl
                roleToCheck="systemTrayIcon.edit"
                render={
                  <ToggleButton
                    size="small"
                    onChange={(data) => enabledRebranding(data, 'trayIcon')}
                    value={agentCustomizationData?.trayIcon?.enabled}
                    labels={{
                      on: getTranslatedText(
                        intl,
                        'admin.systemTrayIcon.enabled.status'
                      ),
                      off: getTranslatedText(
                        intl,
                        'admin.systemTrayIcon.disabled.status'
                      )
                    }}
                  />
                }
              />
            </div>
            <div className="flex actr">
              <div className="mr-20">
                <Attachments
                  nestedObject={'trayIcon'}
                  attachmentType={ALL_MODULES.trayIcon}
                  loading={actionLoading}
                  agentCustomizationData={agentCustomizationData.trayIcon}
                  downloadURL={downloadURL.trayIcon}
                  handleUpload={handleUpload}
                  intl={props.intl}
                  description={
                    <FormattedMessage id="admin.mspDetails.logo.desc" />
                  }
                  title={<FormattedMessage id="admin.mspDetails.logo.title" />}
                  isSystemTrayEditAccess={isSystemTrayEditAccess}
                />
              </div>
              <div className="f11 mr-100">
                <div className="mt-m-20 mw-350 ">
                  <TextField
                    size="large"
                    noborder
                    fontSize="fontLarge"
                    placeholder={getTranslatedText(
                      intl,
                      'admin.settings.systemtry.input.placeholder'
                    )}
                    value={agentCustomizationData?.trayIcon?.name}
                    onChange={(data) => onInputChange(data, 'trayIcon', 'name')}
                    disabled={!isSystemTrayEditAccess}
                  />
                </div>
              </div>
            </div>
          </div> */}
          {/* <div className="p2040">
            <MenuOptions>
              <div className="flex">
                <div className="fx fx2 mb-10">
                  <Heading element="h3" weight="Bold">
                    <FormattedMessage id="admin.system.tray.menu.options" />
                  </Heading>
                </div>
                <div className="fx fx2 jend">
                  <AccessControl
                    roleToCheck="systemTrayIcon.edit"
                    render={
                      <TrayIconSelect
                        addNewMenuItem={addNewMenuItem}
                        planId={planId}
                      />
                    }
                  />
                </div>
              </div>
              <div>
                <ReactSortable
                  className="sort-wrapper"
                  list={agentCustomizationData?.trayIcon?.menus}
                  handle=".dragHandle"
                  setList={setSortedList}
                  animation={200}
                >
                  {agentCustomizationData?.trayIcon?.menus.map(
                    (item, index) => {
                      const { type } = item;
                      return (
                        <div
                          className="wm-500 visibilityContainer"
                          key={item.menuId}
                        >
                          <ResourceItem
                            verticalAlignment="center"
                            gutter="md"
                            onClick={() => {
                              isSystemTrayEditAccess && handleClick(item);
                            }}
                          >
                            <div className="flex actr fx-sb">
                              <Heading element="h3">
                                <TextStyle>
                                  <div className="flex center">
                                    <AccessControl
                                      roleToCheck="systemTrayIcon.edit"
                                      render={
                                        <div className="dragHandle mr-20 cursor-move">
                                          <SVGIcon name="Reorder" />
                                        </div>
                                      }
                                    />
                                    <div className="ptr rounded-4px">
                                      <span className="fx">
                                        <span className="w-35 fx jcenter acenter mr8">
                                          <SVGIcon
                                            size="sm"
                                            name={
                                              TRACY_ICON_ACTION_ITEMS[item.type]
                                                .icon
                                            }
                                          />
                                        </span>
                                        <TextStyle>{item?.name}</TextStyle>
                                      </span>
                                    </div>
                                  </div>
                                </TextStyle>
                              </Heading>
                              <AccessControl
                                roleToCheck="systemTrayIcon.edit"
                                render={
                                  <div className="visibilityActions">
                                    <DeleteModal
                                      description={
                                        <FormattedMessage id="admin.tray.icon.deleteItem.modalDescription" />
                                      }
                                      handleDeleteConfirmation={() =>
                                        deleteMenuItem(item)
                                      }
                                      title={
                                        <FormattedMessage id="admin.tray.icon.deleteItem.modalDescription.title" />
                                      }
                                      modalActivator={
                                        <div className="delete-button">
                                          <SVGIcon name="Delete" />
                                        </div>
                                      }
                                    />
                                  </div>
                                }
                              />
                            </div>
                          </ResourceItem>
                        </div>
                      );
                    }
                  )}
                </ReactSortable>
                {sheetData.open && (
                  <Sheet open={sheetData.open} onClose={toggleSheetActive}>
                    <SystemTrayMenuOptionForm
                      sheetData={sheetData}
                      agentData={agentCustomizationData}
                      toggleSheetActive={toggleSheetActive}
                      meta={{
                        title: sheetData?.menuData?.menuId
                          ? getTranslatedText(
                              intl,
                              'admin.system.tray.edit.menu.option'
                            )
                          : getTranslatedText(
                              intl,
                              'admin.system.tray.new.menu.option'
                            )
                      }}
                      updateData={updateData}
                      planId={planId}
                    />
                  </Sheet>
                )}
              </Wrapper>
            </MenuOptions>
          </div> */}
          {/* <div className="aend p2040">
            <div className="mb-20">
              <Heading element="h3" weight="Bold">
                <FormattedMessage id="admin.system.tray.menu.preview" />
              </Heading>
            </div>
            <div className="mt-20 mb-30">
              <Banner status="info">
                <DisplayText>
                  <FormattedMessage id="admin.system.tray.icon.banner.message" />
                </DisplayText>
              </Banner>
            </div>
            <div className="fx">
              {AVAILABLE_OS_TYPE.map((osType) => {
                const url = agentCustomizationData?.trayIcon?.logoFileName
                  ? getDownloadURL({
                      module: 'TRAY_ICON',
                      details: {
                        fileName: agentCustomizationData?.trayIcon?.logoFileName
                      }
                    })
                  : null;
                return (
                  <div className="fx2 p8">
                    <div className="d-in-f col min-width-300 w400">
                      <Card
                        title={
                          <>
                            <BackgroundIcon
                              url={
                                downloadURL.trayIcon
                                  ? `url(${downloadURL.trayIcon})`
                                  : url
                                  ? `url(${url})`
                                  : `url(${envConfig.imageRoot}svg/Org.svg)`
                              }
                              className="org-image mr16"
                              opacity={
                                !downloadURL.trayIcon && !url ? '0.4' : ''
                              }
                            />
                            <Heading element="h2">
                              {agentCustomizationData?.trayIcon?.name}
                            </Heading>
                          </>
                        }
                      >
                        {agentCustomizationData?.trayIcon?.menus
                          .filter((item) =>
                            filterScriptsWithoutData(item, osType)
                          )
                          .map((li, i) => {
                            if (li)
                              return (
                                <Card.Section key={i}>
                                  <Tooltip
                                    title={dompurifier(li.toolTipText) || ''}
                                    position="top-start"
                                    inertia
                                    delay={300}
                                    trigger="mouseenter"
                                    animation="shift"
                                    animateFill={false}
                                  >
                                    <div className="flex center">
                                      <span className="w-35 fx jcenter acenter mr16">
                                        <SVGIcon
                                          size="sm"
                                          name={
                                            TRACY_ICON_ACTION_ITEMS[li.type]
                                              .icon
                                          }
                                        />
                                      </span>
                                      <TextStyle>{li.name}</TextStyle>
                                    </div>
                                  </Tooltip>
                                </Card.Section>
                              );
                          })}
                      </Card>
                      <div className="mt-20">
                        <TextStyle weight="Bold">
                          <div className="flex actr jctr g5">
                            <SVGIcon name={osType.icon} />
                            {osType.title}
                          </div>
                        </TextStyle>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div> */}
        </div>
      </Page>
    </div>
  );
}

export default injectIntl(withRouter(withLDConsumer()(SystemTrayIconPage)));
